import {
  Box,
  ButtonBase,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { MouseEvent, startTransition, useMemo, useState } from "react";
import {
  DatePeriod,
  Filter,
  FilterView,
  LogModel,
  QueryFilterTemplate,
  SortDirection,
} from "../../services/swagger";
import FilterInput, { allowedOperators } from "../molecules/FilterInput";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  filtersToParamString,
  paramStringToObject,
} from "../../helpers/params";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import useBreakpoint from "../../hooks/useBreakpoint";
import { capitalizeFirstLetter } from "../../helpers/string";
import FieldLabel from "../atoms/FieldLabel";
import TableViewIcon from "@mui/icons-material/TableView";
import FilterViews from "./FilterViews";

interface FilterBoxProps {
  open: boolean;
  sortField: string;
  sortDirection: SortDirection;
  onClose?: () => void;
  filterTemplate: QueryFilterTemplate[];
  onChange?: (
    filters: Filter[],
    sort?: { field?: string; direction?: SortDirection }
  ) => void;
}

export default function FilterBox(props: FilterBoxProps) {
  const { t: tLabels } = useTranslation("FilterLabels");
  const { t: tFBox } = useTranslation("FilterBox");
  const [searchParams, setSearchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<any>();
  const [filtersApplied, setFiltersApplied] = useState<Filter[]>([]);
  const [advancedFilters, setAdvancedFilters] = useState(false);
  const [savedViewsMenu, setSavedViewsMenu] = useState(false);
  const [filterView, setFilterView] = useState("");
  const breakpoint = useBreakpoint();

  const getDefaultFilters: () => Filter[] = useMemo(() => {
    return () =>
      props.filterTemplate.map((filter) => ({
        field: filter.field,
        operator: allowedOperators[filter.valueType][0],
        value: "",
        type: filter.valueType,
      }));
  }, [props.filterTemplate]);

  const handleApplyFilters = (
    filtersOverride?: Filter[],
    sortOverride?: { field: string; direction: SortDirection }
  ) => {
    const filtersToApply = filtersOverride || filtersApplied;
    const sortToApply = sortOverride || {
      field: props.sortField,
      direction: props.sortDirection,
    };
    props.onChange?.(filtersToApply, sortToApply);
  };

  const handleAddFilter = (filter: { name: string; value: string }) => {
    const newFilter = props.filterTemplate.find(
      (f) => f.field === filter.value
    );
    if (!newFilter) return;
    setFiltersApplied([
      ...filtersApplied,
      {
        field: newFilter.field,
        operator: allowedOperators[newFilter.valueType][0],
        type: newFilter.valueType,
        value: "",
      },
    ]);
    setAnchorEl(null);
  };

  const handleClearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilterView("");
    setFiltersApplied(defaultFilters);
    handleApplyFilters(defaultFilters);
  };

  const handleAdvancedFiltersClick = () => {
    setAdvancedFilters((prev) => {
      const newValue = !prev;

      if (!newValue) {
        const defaultFilters = getDefaultFilters();
        setFiltersApplied(defaultFilters);
        handleApplyFilters(defaultFilters);
      }

      return newValue;
    });
  };

  const handleToggleSavedViewsMenu = () => {
    setSavedViewsMenu((e) => !e);
  };

  const handleChangeFilterView = (filterView: FilterView) => {
    if (props.filterTemplate.length < 1) return;
    setFilterView(filterView._id || "");
    const filters = getDefaultFilters();
    const addedFilters: Filter[] = [];
    let duplicate = false;
    for (const filterIndex in filterView.filters) {
      const filter = filterView.filters[filterIndex];
      if (addedFilters.some((f) => f.field === filter.field)) {
        filters.push(filter);
        addedFilters.push(filter);
        duplicate = true;
        continue;
      }
      for (const existingFilterIndex in filters) {
        const existingFilter = filters[existingFilterIndex];
        if (existingFilter.field === filter.field) {
          filters[existingFilterIndex] = filter;
          addedFilters.push(filter);
          continue;
        }
      }
    }
    if (duplicate) setAdvancedFilters(true);
    setFiltersApplied(filters);
    handleApplyFilters(filters);
  };

  useDidMountEffect(() => {
    if (props.filterTemplate.length < 1) return;
    if (filtersApplied.length > 0) return;
    const defaultFilters = getDefaultFilters();
    const loadedFilters: Filter[] | null = searchParams.get("filters")
      ? (paramStringToObject(searchParams.get("filters") as any) as Filter[])
      : null;
    if (loadedFilters === null) {
      setSearchParams(
        (sp) => {
          sp.set("filters", filtersToParamString(defaultFilters));
          return sp;
        },
        { replace: true }
      );
      return setFiltersApplied(defaultFilters);
    } else {
      const newFilters: Filter[] = [];
      const filterFields: string[] = [];
      for (const filter of loadedFilters) {
        if (
          filterFields.includes(filter.field) ||
          loadedFilters.some(
            (filter) => filter.operator !== allowedOperators[filter.type][0]
          )
        ) {
          setAdvancedFilters(true);
          continue;
        }
        filterFields.push(filter.field);
      }
      const extraFilters: Filter[] = [];
      defaultFilters.forEach((filter) => {
        const matchedFilters = loadedFilters.filter(
          (f) => f.field === filter.field
        );
        if (matchedFilters.length === 0) newFilters.push(filter);
        else if (matchedFilters.length > 0)
          newFilters.push({
            ...filter,
            value: matchedFilters[0].value,
            operator: matchedFilters[0].operator,
          });
        for (let i = 1; i < matchedFilters.length; i++) {
          extraFilters.push(matchedFilters[i]);
        }
      });
      setFiltersApplied([...newFilters, ...extraFilters]);
    }
  }, [props.filterTemplate, props.open, searchParams]);

  return (
    <Stack
      spacing={2}
      sx={{
        bgcolor: "background.default",
        width: 1,
        // zIndex: 10,
        borderRadius: 1,
        boxShadow: 3,
        height: 1,
        p: 2,
        overflow: "auto",
      }}
    >
      <Stack
        direction={"row"}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h5">{tFBox("Filters")}</Typography>
        <Stack direction="row" spacing={2}>
          <Tooltip placement="bottom" arrow title={tFBox("ResetAll")}>
            <IconButton size="small" color="error" onClick={handleClearFilters}>
              <ClearAllIcon />
            </IconButton>
          </Tooltip>
          <Tooltip placement="bottom" arrow title={tFBox("SavedViews")}>
            <IconButton
              size="small"
              onClick={handleToggleSavedViewsMenu}
              sx={{
                color: savedViewsMenu ? "primary.contrastText" : "primary.main",
                bgcolor: savedViewsMenu ? "primary.main" : "inherit",
                ":hover": {
                  color: savedViewsMenu ? "primary.contrastText" : undefined,
                  bgcolor: savedViewsMenu ? "primary.light" : undefined,
                },
              }}
            >
              <TableViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip placement="bottom" arrow title={tFBox("AdvancedFilters")}>
            <IconButton
              size="small"
              onClick={handleAdvancedFiltersClick}
              sx={{
                color: advancedFilters
                  ? "primary.contrastText"
                  : "primary.main",
                bgcolor: advancedFilters ? "primary.main" : "inherit",
                ":hover": {
                  color: advancedFilters ? "primary.contrastText" : undefined,
                  bgcolor: advancedFilters ? "primary.light" : undefined,
                },
              }}
            >
              <TuneIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Box>
        <Collapse in={savedViewsMenu}>
          <FilterViews
            model={LogModel.Ticket}
            filterView={filterView}
            onChange={handleChangeFilterView}
          />
        </Collapse>
        <Divider />
      </Box>
      <Stack
        spacing={2}
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <Stack spacing={2} flexGrow={1} overflow="auto">
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            {[
              ...(props.filterTemplate?.map((filter) => {
                return {
                  name: filter.name,
                  value: filter.field,
                };
              }) || []),
            ].map((filter) => {
              return (
                <MenuItem
                  key={filter.value}
                  sx={{ minWidth: 200 }}
                  onClick={() => handleAddFilter(filter)}
                >
                  {tLabels(filter.name)}
                </MenuItem>
              );
            })}
          </Menu>
          {filtersApplied.length > 0 && (
            <Stack spacing={2}>
              {filtersApplied.map((filter, i) => {
                const template = props.filterTemplate.find(
                  (f) => f.field === filter.field
                );
                const isExtra =
                  filtersApplied.findIndex((e) => e.field === filter.field) !==
                  i;
                if (!template) return null;
                return (
                  <FilterInput
                    key={i}
                    field={template.field}
                    label={tLabels(template.name)}
                    list={template.listOptions}
                    type={template.valueType}
                    value={filter.value}
                    operator={filter.operator}
                    onDelete={
                      isExtra
                        ? () => {
                            const newFiltersApplied = [...filtersApplied];
                            newFiltersApplied.splice(i, 1);
                            setFiltersApplied(newFiltersApplied);
                            handleApplyFilters(newFiltersApplied);
                          }
                        : undefined
                    }
                    onChange={(newFilter) => {
                      filter.operator = newFilter.operator;
                      filter.value = newFilter.value;
                      filter.dateOptions = newFilter.dateOptions;
                      const newFilters = [...filtersApplied];
                      setFiltersApplied(newFilters);
                      startTransition(() => handleApplyFilters(newFilters));
                    }}
                    dateIndicator={filter.dateOptions?.indicator}
                    datePeriod={filter.dateOptions?.period || DatePeriod.Month}
                    advancedFilters={advancedFilters}
                  />
                );
              })}
            </Stack>
          )}

          {advancedFilters && (
            <Tooltip title={tFBox("AddFilter")}>
              <ButtonBase
                onClick={(e) => setAnchorEl(e.currentTarget)}
                disableRipple
                sx={{ width: "auto", alignSelf: "flex-start" }}
              >
                <Stack direction={"row"} spacing={1}>
                  <AddCircleOutlineOutlinedIcon color="primary" />
                  <Typography variant="button" color="primary">
                    {tFBox("AddFilter")}
                  </Typography>
                </Stack>
              </ButtonBase>
            </Tooltip>
          )}
        </Stack>
        {!breakpoint.lg && (
          <>
            <Divider />
            <Box>
              <Grid container spacing={1}>
                <Grid size={7}>
                  <FieldLabel fontSize={12}>{tFBox("SortBy")}</FieldLabel>
                  <Select
                    className="borderless-select"
                    value={props.sortField}
                    sx={{ width: 1 }}
                    onChange={(e) =>
                      handleApplyFilters(undefined, {
                        field: e.target.value,
                        direction: props.sortDirection,
                      })
                    }
                  >
                    {props.filterTemplate
                      .filter((filter) => filter.sortable)
                      .map((filter) => (
                        <MenuItem key={filter.field} value={filter.field}>
                          {filter.field
                            .split(/(_|\.)/g)
                            .filter((e) => e !== "_" && e !== ".")
                            .map((e) => capitalizeFirstLetter(e))
                            .join(" ")}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>
                <Grid size={5}>
                  <FieldLabel fontSize={12}>{tFBox("OrderBy")}</FieldLabel>
                  <Select
                    className="borderless-select"
                    value={props.sortDirection}
                    onChange={(e) =>
                      handleApplyFilters(undefined, {
                        field: props.sortField,
                        direction: e.target.value as SortDirection,
                      })
                    }
                    sx={{ width: 1, border: "none" }}
                  >
                    <MenuItem value={SortDirection.Asc}>
                      {tFBox("Ascending")}
                    </MenuItem>
                    <MenuItem value={SortDirection.Desc}>
                      {tFBox("Descending")}
                    </MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Stack>
    </Stack>
  );
}
